import React, { useState } from "react";
import DataGrid, { Column, Export, HeaderFilter, Pager, Paging, SearchPanel } from "devextreme-react/data-grid";
import { DateFormat } from "../../../helpers/fixcodes";
import { activityText, clubText, genderText, statusTone } from "./dispatchRules";
import RideActions, { actionsWidth, mostActions } from "./RideActions";

const DT = { dataType: "datetime", format: DateFormat.DisplayDateTime24NorthFormat };
const SEARCHABLE = { allowSearch: true, headerFilter: { allowSearch: true } };
/** Columns with a calculated value (no dataField): DevExtreme turns their filter / sort off unless asked. */
const CALCULATED = { ...SEARCHABLE, allowFiltering: true, allowHeaderFiltering: true, allowSorting: true };

/** Activity code + location (non fly) or the flight. */
export const ActivityCell = ({ ride }) =>
  ride.activityLocationCode ? (
    <span className="dsp-activity">
      <span>
        <i className="fas fa-map-marker-alt" aria-hidden="true" /> {ride.activityCode ?? ""}
      </span>
      <span className="text-muted">
        <i className="fas fa-building" aria-hidden="true" /> {ride.activityLocationCode}
      </span>
    </span>
  ) : (
    <span className="dsp-activity">
      <span>
        <i className="fas fa-plane-departure" aria-hidden="true" /> {activityText(ride)}
      </span>
    </span>
  );

/** Ride status as a coloured chip (same colours the rows used to have). */
export const StatusChip = ({ ride }) => <span className={`dsp-chip is-${statusTone(ride)}`}>{ride.tripStatus || "—"}</span>;

/**
 * Desktop grid of the rides: same columns as before, status as a chip and a thin coloured accent on the row
 * (classes, no inline colours), the actions in the fixed first column, search and Excel export.
 */
export default function DispatchGrid({ rides, gridRef, onTrack, onReplay, onMobile }) {
  // The actions column fits the rows on screen: two buttons when a visible ride can be tracked / replayed, else one.
  // Checked each time the grid is drawn (search, tile, filter, sort, page) and kept in state (the width prop only).
  const [actionsColumnWidth, setActionsColumnWidth] = useState(() => actionsWidth(1));
  const fitActions = (e) => {
    const visible = e.component
      .getVisibleRows()
      .filter((r) => r.rowType === "data")
      .map((r) => r.data);
    const width = actionsWidth(mostActions(visible));
    setActionsColumnWidth((current) => (current === width ? current : width));
  };
  return (
    <DataGrid
      ref={gridRef}
      dataSource={rides}
      className="dsp-grid"
      columnAutoWidth
      showColumnLines={false}
      showRowLines
      showBorders
      allowColumnResizing
      allowColumnReordering
      repaintChangesOnly
      onContentReady={fitActions}
      onRowPrepared={(e) => {
        if (e.rowType === "data") e.rowElement.classList.add("dsp-row", `is-${statusTone(e.data)}`);
      }}
    >
      <Column
        caption=""
        fixed
        width={actionsColumnWidth}
        alignment="center"
        allowSorting={false}
        allowFiltering={false}
        allowExporting={false}
        allowHiding={false}
        cssClass="dsp-act-cell"
        cellRender={({ data }) => <RideActions ride={data} onTrack={onTrack} onReplay={onReplay} onMobile={onMobile} />}
      />
      <Column dataField="rideId" caption="Ride Id #" dataType="string" {...SEARCHABLE} />
      <Column dataField="tripStatus" caption="Ride status" {...SEARCHABLE} cellRender={({ data }) => <StatusChip ride={data} />} />
      <Column dataField="activityType" caption="Duty type" {...SEARCHABLE} />
      <Column
        dataField="activityDisplay"
        caption="Activity / Flight"
        width={190}
        calculateCellValue={activityText}
        {...SEARCHABLE}
        cellRender={({ data }) => <ActivityCell ride={data} />}
      />
      <Column dataField="employeeNo" caption="Staff ID #" dataType="string" {...SEARCHABLE} />
      <Column dataField="paxName" caption="Staff name" {...SEARCHABLE} />
      <Column dataField="paxMobileNo" caption="Staff mobile" {...SEARCHABLE} />
      <Column dataField="crewRank" caption="Staff rank" alignment="center" {...SEARCHABLE} />
      <Column
        name="gender"
        caption="Gender"
        calculateCellValue={genderText}
        {...CALCULATED}
        cellRender={({ data }) => (
          <span className="dsp-activity">
            {data.gender && <span>{data.gender.toUpperCase()}</span>}
            <span className="text-muted">{data.crewType}</span>
          </span>
        )}
      />
      <Column dataField="transporterName" caption="Service provider" {...SEARCHABLE} />
      <Column dataField="pickupLocation" caption="Pick-up" width={210} {...SEARCHABLE} />
      <Column dataField="dropoffLocation" caption="Drop-off" width={210} {...SEARCHABLE} />
      <Column dataField="flightNo" caption="Flight no." {...SEARCHABLE} />
      <Column dataField="flightTime" caption="Flight time" {...DT} {...SEARCHABLE} />
      <Column dataField="flightTerminal" caption="Terminal" {...SEARCHABLE} />
      <Column dataField="flightATA" caption="Flight actual time" {...DT} {...SEARCHABLE} />
      <Column dataField="pickupTime" caption="Pickup time" {...DT} {...SEARCHABLE} />
      <Column dataField="dutyReportingTime" caption="Reporting time" {...DT} {...SEARCHABLE} />
      <Column dataField="deBriefingTime" caption="Debriefing time" {...DT} {...SEARCHABLE} />
      <Column dataField="vehicleNo" caption="Vehicle" {...SEARCHABLE} />
      <Column dataField="driverName" caption="Driver" {...SEARCHABLE} />
      <Column dataField="assignedStatus" caption="Assigned status" {...SEARCHABLE} />
      <Column dataField="boardingOtp" caption="Boarding OTP" dataType="string" alignment="center" {...SEARCHABLE} />
      <Column dataField="otpVerifiedStatus" caption="OTP verify status" {...SEARCHABLE} />
      <Column
        name="clubbing"
        caption="Clubbing status"
        calculateCellValue={clubText}
        {...CALCULATED}
        cellRender={({ data }) => (
          <span className={data.isClubbed ? "" : "text-muted"}>
            <i className={`fas fa-${data.isClubbed ? "link" : "unlink"}`} aria-hidden="true" /> {clubText(data)}
          </span>
        )}
      />
      <Column dataField="assignedTime" caption="Assigned time" {...DT} {...SEARCHABLE} />
      <Column dataField="locationTime" caption="On location" {...DT} {...SEARCHABLE} />
      <Column dataField="loadTime" caption="Boarded" {...DT} {...SEARCHABLE} />
      <Column dataField="unloadTime" caption="Reached" {...DT} {...SEARCHABLE} />
      <Column dataField="completedTime" caption="Completed" {...DT} {...SEARCHABLE} />
      <Column dataField="activityStartTime" caption="Activity time" {...DT} {...SEARCHABLE} />
      <HeaderFilter visible />
      <Paging defaultPageSize={20} />
      <Pager displayMode="compact" showPageSizeSelector allowedPageSizes={[10, 20, 50, 100]} showInfo visible />
      <SearchPanel visible width={240} placeholder="Search rides..." />
      <Export enabled allowExportSelectedData={false} fileName="CrewTransfer" />
    </DataGrid>
  );
}
